import { ArrowLeft } from 'lucide-react'
import { useCallback, useState } from 'react'
import { Link } from 'react-router'
import { useAudiences } from '@/api/hooks/use-audiences'
import { useWpConnections } from '@/api/hooks/use-wordpress'
// TestConnection is rendered by StepRow for step 6
import { WP_PLUGIN_DOWNLOAD_URL } from '@/lib/wp-plugin-url'
import { GenerateTokenInline } from '@/pages/wordpress-setup/generate-token-inline'
import { SelectAudienceInline } from '@/pages/wordpress-setup/select-audience-inline'
import { StepRow } from '@/pages/wordpress-setup/setup-guide-row'
import { SETUP_STEPS, TOTAL_STEPS } from '@/pages/wordpress-setup/setup-guide-steps'
import { SubscribeFormInline } from '@/pages/wordpress-setup/shortcode-inline'

// All state is session-based — no localStorage. Fresh setup each visit.

function getCompletedSteps(
  tokenGenerated: boolean,
  hasSelectedAudience: boolean,
  shortcodeConfirmed: boolean,
  testPassed: boolean,
): Set<number> {
  const completed = new Set<number>()
  // Step 1 (install plugin) is always manual — we can't verify it
  // Step 2 is done when the user generates a token in this session
  if (tokenGenerated) {
    completed.add(2)
  }
  // Step 3 (paste token in WP) is manual — we can't verify it from here
  // Step 4 is done when user selects an audience
  if (hasSelectedAudience) {
    completed.add(4)
  }
  // Step 5 is manual confirmation
  if (shortcodeConfirmed) {
    completed.add(5)
  }
  // Step 6 is done when the test passes
  if (testPassed) {
    completed.add(6)
  }
  return completed
}

export function SetupGuidePage(): React.JSX.Element {
  const { data: audiencesData } = useAudiences()
  useWpConnections()

  const audiences = audiencesData?.data ?? []
  // Session-based state — fresh each time the setup page is visited
  const [tokenGenerated, setTokenGenerated] = useState(false)
  const [selectedAudienceId, setSelectedAudienceId] = useState('')
  const effectiveAudienceId =
    selectedAudienceId || (audiences.length === 1 ? (audiences[0]?.id ?? '') : '')
  const hasSelectedAudience = effectiveAudienceId !== ''

  const handleAudienceChange = useCallback((id: string) => {
    setSelectedAudienceId(id)
  }, [])

  const handleTokenGenerated = useCallback(() => {
    setTokenGenerated(true)
  }, [])

  const renderInlineContent = useCallback(
    (stepId: number): React.ReactNode => {
      if (stepId === 1) return (
        <a href={WP_PLUGIN_DOWNLOAD_URL} download className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">
          Download Plugin (one-email.zip)
        </a>
      )
      if (stepId === 2) return <GenerateTokenInline onTokenGenerated={handleTokenGenerated} />
      if (stepId === 4) return <SelectAudienceInline onAudienceChange={handleAudienceChange} />
      if (stepId === 5) return <SubscribeFormInline audienceId={effectiveAudienceId} />
      return undefined
    },
    [effectiveAudienceId, handleAudienceChange, handleTokenGenerated],
  )

  const [shortcodeConfirmed, setShortcodeConfirmed] = useState(false)
  const [expandedStep, setExpandedStep] = useState<number | null>(null)
  const [testPassed, setTestPassed] = useState(false)

  const completed = getCompletedSteps(
    tokenGenerated,
    hasSelectedAudience,
    shortcodeConfirmed,
    testPassed,
  )
  const allDone = completed.size === TOTAL_STEPS
  const completedCount = completed.size
  const progressPercent = Math.round((completedCount / TOTAL_STEPS) * 100)

  function handleShortcodeToggle(): void {
    setShortcodeConfirmed(!shortcodeConfirmed)
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/wordpress"
        className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to WordPress
      </Link>

      <div>
        <h1 className="text-2xl font-semibold text-text-primary">WordPress Setup Guide</h1>
        <p className="mt-1 text-sm text-text-secondary">
          {allDone
            ? 'Setup complete! Your WordPress site is connected and ready.'
            : 'Follow these steps to connect your WordPress site.'}
        </p>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="h-2 flex-1 rounded-full bg-surface-2">
          <div
            className="h-2 rounded-full bg-brand-500 transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <span className="text-xs font-medium text-text-secondary">
          {completedCount}/{TOTAL_STEPS}
        </span>
      </div>

      {/* Steps */}
      <div className="space-y-2">
        {SETUP_STEPS.map((step) => {
          const isDone = completed.has(step.id)
          const isExpanded = expandedStep === step.id

          return (
            <StepRow
              key={step.id}
              step={step}
              isDone={isDone}
              isExpanded={isExpanded}
              onToggle={() => setExpandedStep(isExpanded ? null : step.id)}
              shortcodeConfirmed={shortcodeConfirmed}
              onShortcodeToggle={handleShortcodeToggle}
              audienceId={effectiveAudienceId}
              hasAudience={hasSelectedAudience}
              renderContent={isExpanded ? renderInlineContent(step.id) : undefined}
              onTestPassed={() => setTestPassed(true)}
            />
          )
        })}
      </div>
    </div>
  )
}
